import { useMutation, useQuery } from "@apollo/client";
import {
  Card,
  Drawer,
  LoadingOverlay,
  ScrollArea,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { CALL_DRIVER_FOR_ORDER } from "apollo/mutuations";
import { MY_GET_ORDERS } from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import ManageOrderModal from "components/Order/ManageOrderModal";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import Pusher from "pusher-js";
import { useEffect, useState } from "react";
import { FiEye } from "react-icons/fi";
import { IoIosCall } from "react-icons/io";
import { formatStatus } from "utiles/url";
import OrderCard from "./card";

const MyOrders = () => {
  const [dropoffStatus, setDropoffStatus] = useState(null);
  const [callingOrders, setCallingOrders] = useState({}); // Use object to track calling state per order

  const [size, setSize] = useState("10");
  const [openedEdit, setOpenedEdit] = useState(false);
  const [editId, setEditId] = useState();
  const roles = JSON.parse(localStorage.getItem("roles")) || [];
  const hasManagerPermission = roles.some(
    (permission) => permission === "warehouse_manager",
  );
  const theme = useMantineTheme();
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const { data, loading, fetchMore, refetch } = useQuery(MY_GET_ORDERS, {
    variables: {
      status: dropoffStatus,
      search: searchValue,
      first: parseInt(size),
      page: activePage,
      ordered_by: [
        {
          column: "CREATED_AT",
          order: "DESC",
        },
      ],
    },
    onCompleted: (data) => {
      setTotal(data?.myOrders.paginatorInfo.lastPage);
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message: error?.graphQLErrors?.[0]?.message || "Failed to Get Orders",
      });
    },
  });

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  useEffect(() => {
    refetch();
    localStorage.removeItem("orderCount");
    window.dispatchEvent(new Event("orderCount-changed"));
  }, []);

  useEffect(() => {
    const pusher = new Pusher("83f49852817c6b52294f", {
      cluster: "mt1",
    });

    const notificationChannel = pusher.subscribe("notification");

    notificationChannel.bind("new-item-created", function (newOrder) {
      if (!dropoffStatus || newOrder.state === dropoffStatus) {
        refetch()
          .then(({ data }) => {
            const updatedOrders = data?.myOrders?.data || [];
            setTotal(data?.myOrders.paginatorInfo.lastPage);
            setOrders(updatedOrders);
          })
          .catch((error) => {
            console.error("Error fetching updated orders:", error);
          });
      }
    });

    return () => {
      pusher.disconnect();
    };
  }, [dropoffStatus, refetch]);

  const [confirmedSearch, setConfirmedSearch] = useState("");
  const [orders, setOrders] = useState([]);

  const clearFilter = () => {
    setDropoffStatus(null);
    setSearchValue("");
  };

  const clearInput = () => {
    setSearchValue("");
    setDropoffStatus(null);
    setConfirmedSearch("");
  };

  const [confirmOrderForDelivery] = useMutation(CALL_DRIVER_FOR_ORDER, {
    refetchQueries: [
      {
        query: MY_GET_ORDERS,
        variables: {
          status: dropoffStatus,
          search: searchValue,
          first: parseInt(size),
          page: activePage,
          ordered_by: [
            {
              column: "CREATED_AT",
              order: "DESC",
            },
          ],
        },
      },
    ],
    onCompleted(data) {
      setCallingOrders((prev) => ({
        ...prev,
        [data.confirmOrderForDelivery.id]: false, // Reset calling state for this order
      }));
      showNotification({
        color: "green",
        title: "Success",
        message: "Driver called successfully!",
      });
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === data.confirmOrderForDelivery.id
            ? { ...order, status: "READY_FOR_DELIVERY" }
            : order,
        ),
      );
    },
    onError(error) {
      setCallingOrders((prev) => ({
        ...prev,
        [error?.variables?.id]: false, // Reset calling state on error
      }));
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          "Driver not called successfully!",
      });
    },
  });

  const handleRecallDriver = (id) => {
    setCallingOrders((prev) => ({ ...prev, [id]: true })); // Set calling state for specific order
    confirmOrderForDelivery({ variables: { id: id } });
  };

  const headerData = [
    {
      label: "Retailer",
      key: "retailer",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData?.retailer?.name}</span>;
      },
    },
    {
      label: "Warehouse",
      key: "warehouse",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData?.warehouse?.name}</span>;
      },
    },
    {
      label: "Order Code",
      key: "order+code",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData?.order_code}</span>;
      },
    },
    {
      label: "Status",
      key: "status",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{formatStatus(rowData.status)}</span>;
      },
    },
    {
      label: "Product Count",
      key: "productSkuCount",
      sortable: false,
      searchable: false,
      render: (rowData) => {
        return <span>{rowData.productSkuCount}</span>;
      },
    },
    {
      label: "Total Price",
      key: "total_price",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.total_price}</span>;
      },
    },
    {
      label: "Order Date",
      key: "created_at",
      sortable: true,
      searchable: false,
      render: (rowData) => {
        return <span>{rowData.created_at.substring(0, 10)}</span>;
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      render: (rowData) => {
        return (
          <div style={{ width: "153px", display: "flex", gap: "6px" }}>
            <ActionButton
              label="View"
              color="success"
              onClick={() => ManageOrder(`${rowData.id}`)}
            >
              <FiEye size={15} />
            </ActionButton>
            {rowData.status === "ORDERED" &&
              hasManagerPermission &&
              rowData.delivery_option === "COMPANY_DELIVERY" && (
                <>
                  {callingOrders[rowData.id] ? (
                    <ActionButton label="Calling" color="call"></ActionButton>
                  ) : (
                    <ActionButton
                      label="Call"
                      color="call"
                      onClick={() => handleRecallDriver(`${rowData.id}`)}
                    >
                      <IoIosCall size={15} />
                    </ActionButton>
                  )}
                </>
              )}
          </div>
        );
      },
    },
  ];

  const handleChange = (currentPage) => {
    fetchMore({
      variables: {
        first: parseInt(size),
        page: currentPage,
      },
    });
    setActivePage(currentPage);
  };

  const ManageOrder = (id) => {
    setOpenedEdit(true);
    setEditId(id);
  };
  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };
  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Drawer
        opened={openedEdit}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        title="Managing Orders"
        padding="xl"
        onClose={() => setOpenedEdit(false)}
        position="bottom"
        size="80%"
      >
        <ManageOrderModal
          total={total}
          setTotal={setTotal}
          activePage={activePage}
          setActivePage={setActivePage}
          setOpenedEdit={setOpenedEdit}
          editId={editId}
        />
      </Drawer>

      <Card shadow="sm" p="lg">
        <OrderCard
          onCardClick={setDropoffStatus}
          handelSearch={setSearchValue}
          clearFilter={clearFilter}
          selectedStatus={dropoffStatus}
        />
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            handelSearch={handleManualSearch}
            clearInput={clearInput}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            dropoffStatus={dropoffStatus}
            loading={loading}
            data={orders.length ? orders : data?.myOrders?.data || []}
            size={size}
            handlePageSizeChange={handlePageSizeChange}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default MyOrders;
